import { useState, useMemo, useEffect, useRef } from "react";
import type { Product } from "@/lib/api";
import { useReviews } from "@/hooks/useReviews";

interface ProductReviewsProps {
  product: Product;
}

const AVATAR_COLORS = [
  "bg-stone-200 text-stone-600",
  "bg-rose-100 text-rose-600",
  "bg-amber-100 text-amber-700",
  "bg-emerald-100 text-emerald-700",
  "bg-sky-100 text-sky-700",
  "bg-violet-100 text-violet-700",
];

const FIT_COLORS: Record<string, string> = {
  "Runs Small": "text-amber-600 bg-amber-50",
  "True to Size": "text-emerald-700 bg-emerald-50",
  "Runs Large": "text-sky-700 bg-sky-50",
};

const SORT_OPTIONS = [
  { label: "Most Recent", value: "recent" },
  { label: "Most Helpful", value: "helpful" },
  { label: "Highest Rated", value: "high" },
  { label: "Lowest Rated", value: "low" },
];

const PAGE_SIZE = 5;

export default function ProductReviews({ product }: ProductReviewsProps) {
  const { reviews, loading: reviewsLoading } = useReviews(product.id);
  const [filterRating, setFilterRating] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState("recent");
  const [helpfulVotes, setHelpfulVotes] = useState<Record<string, boolean>>({});
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [expandedReviews, setExpandedReviews] = useState<Record<string, boolean>>({});
  const [animatingIds, setAnimatingIds] = useState<Set<string>>(new Set());
  const listEndRef = useRef<HTMLDivElement>(null);

  // Build rating distribution based on product rating
  const totalReviews = product.reviewCount;
  const avgRating = product.rating;

  const distribution = useMemo(() => {
    const base = [0.05, 0.06, 0.09, 0.22, 0.58].map((pct) =>
      Math.round(totalReviews * pct)
    );
    return [5, 4, 3, 2, 1].map((star, i) => ({ star, count: base[i] }));
  }, [totalReviews]);

  const filtered = useMemo(() => {
    let list = [...reviews];
    if (filterRating !== null)
      list = list.filter((r) => r.rating === filterRating);
    switch (sortBy) {
      case "helpful": list.sort((a, b) => b.helpful - a.helpful); break;
      case "high": list.sort((a, b) => b.rating - a.rating); break;
      case "low": list.sort((a, b) => a.rating - b.rating); break;
      default: list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }
    return list;
  }, [filterRating, sortBy]);

  // Reset visible count when filter or sort changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filterRating, sortBy]);

  const handleHelpful = (id: string) => {
    setHelpfulVotes((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleExpand = (id: string) => {
    setExpandedReviews((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleLoadMore = () => {
    const nextBatch = filtered.slice(visibleCount, visibleCount + PAGE_SIZE).map((r) => r.id);
    setAnimatingIds(new Set(nextBatch));
    setVisibleCount((v) => Math.min(v + PAGE_SIZE, filtered.length));
    // Clear animation flags after transition
    setTimeout(() => setAnimatingIds(new Set()), 600);
  };

  const handleCollapse = () => {
    setVisibleCount(PAGE_SIZE);
    // Scroll back to top of reviews list
    listEndRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const visibleReviews = filtered.slice(0, visibleCount);
  const remaining = filtered.length - visibleCount;
  const progressPct = filtered.length > 0 ? Math.round((visibleCount / filtered.length) * 100) : 100;
  const allLoaded = visibleCount >= filtered.length;

  if (reviewsLoading) {
    return (
      <section className="max-w-7xl mx-auto px-5 lg:px-16 py-16 border-t border-pearl/40">
        <p className="text-sm tracking-widest uppercase text-midnight/40 animate-pulse text-center">Loading reviews...</p>
      </section>
    );
  }

  return (
    <section className="max-w-7xl mx-auto px-5 lg:px-16 py-16 border-t border-pearl/40">
      {/* Section Header */}
      <div className="mb-10">
        <p className="text-[10px] tracking-widest uppercase text-midnight/40 mb-1">What They Say</p>
        <h2
          className="text-2xl md:text-3xl font-light text-midnight"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Customer Reviews
        </h2>
      </div>

      <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
        {/* Left: Summary Panel */}
        <div className="w-full lg:w-72 shrink-0">
          <div className="sticky top-24">
            {/* Big Rating Number */}
            <div className="flex items-end gap-3 mb-5">
              <span
                className="text-6xl font-light text-midnight leading-none"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {avgRating.toFixed(1)}
              </span>
              <div className="pb-1">
                <div className="flex gap-0.5 mb-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i
                      key={i}
                      className={`text-sm ${
                        i < Math.floor(avgRating)
                          ? "ri-star-fill text-moon-gold-dark"
                          : avgRating - i > 0 && avgRating - i < 1
                          ? "ri-star-half-line text-moon-gold-dark"
                          : "ri-star-line text-pearl"
                      }`}
                    />
                  ))}
                </div>
                <p className="text-[11px] text-midnight/40">{totalReviews.toLocaleString()} reviews</p>
              </div>
            </div>

            {/* Star Distribution Bars */}
            <div className="space-y-2 mb-6">
              {distribution.map(({ star, count }) => {
                const pct = Math.round((count / totalReviews) * 100);
                const isActive = filterRating === star;
                return (
                  <button
                    key={star}
                    onClick={() => setFilterRating(isActive ? null : star)}
                    className={`w-full flex items-center gap-2.5 group cursor-pointer ${isActive ? "opacity-100" : "opacity-70 hover:opacity-100"}`}
                  >
                    <span className="text-xs text-midnight/60 w-3 shrink-0">{star}</span>
                    <i className="ri-star-fill text-[10px] text-moon-gold-dark shrink-0" />
                    <div className="flex-1 h-1.5 bg-pearl/60 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          isActive ? "bg-midnight" : "bg-midnight/30 group-hover:bg-midnight/50"
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-midnight/40 w-6 text-right shrink-0">{pct}%</span>
                  </button>
                );
              })}
            </div>

            {/* Fit Summary */}
            <div className="border-t border-pearl/40 pt-5 mb-6">
              <p className="text-[10px] tracking-widest uppercase text-midnight/40 mb-3">Fit Feedback</p>
              <div className="space-y-2">
                {(["Runs Small", "True to Size", "Runs Large"] as const).map((fit) => {
                  const cnt = reviews.filter((r) => r.fit === fit).length;
                  const pct = Math.round((cnt / reviews.length) * 100);
                  return (
                    <div key={fit} className="flex items-center justify-between">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${FIT_COLORS[fit]}`}>
                        {fit}
                      </span>
                      <span className="text-xs text-midnight/40">{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Write Review CTA */}
            <button className="w-full py-3 border border-midnight text-xs tracking-widest uppercase text-midnight hover:bg-midnight hover:text-moonlight transition-all duration-300 cursor-pointer whitespace-nowrap">
              Write a Review
            </button>
          </div>
        </div>

        {/* Right: Reviews List */}
        <div className="flex-1 min-w-0">
          {/* Filters & Sort Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-7 pb-5 border-b border-pearl/40">
            {/* Star Filter Pills */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterRating(null)}
                className={`text-[10px] tracking-wide px-3 py-1.5 border rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  filterRating === null
                    ? "border-midnight bg-midnight text-moonlight"
                    : "border-pearl text-midnight/50 hover:border-midnight hover:text-midnight"
                }`}
              >
                All ({totalReviews})
              </button>
              {distribution.map(({ star, count }) => (
                <button
                  key={star}
                  onClick={() => setFilterRating(filterRating === star ? null : star)}
                  className={`text-[10px] tracking-wide px-3 py-1.5 border rounded-full transition-all cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                    filterRating === star
                      ? "border-midnight bg-midnight text-moonlight"
                      : "border-pearl text-midnight/50 hover:border-midnight hover:text-midnight"
                  }`}
                >
                  <i className="ri-star-fill text-[9px]" /> {star} ({count})
                </button>
              ))}
            </div>

            {/* Sort */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-[11px] tracking-wide border border-pearl bg-transparent text-midnight px-3 py-2 pr-7 appearance-none cursor-pointer focus:outline-none focus:border-midnight"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-midnight/40 pointer-events-none text-sm" />
            </div>
          </div>

          {/* Results count */}
          {filterRating !== null && (
            <p className="text-xs text-midnight/40 mb-5">
              Showing {filtered.length} review{filtered.length !== 1 ? "s" : ""} for {filterRating}-star rating
              <button
                onClick={() => setFilterRating(null)}
                className="ml-2 text-midnight underline cursor-pointer"
              >
                Clear
              </button>
            </p>
          )}

          {/* Scroll anchor */}
          <div ref={listEndRef} />

          {/* Review Cards */}
          <div className="space-y-8">
            {visibleReviews.map((review, idx) => {
              const colorClass = AVATAR_COLORS[idx % AVATAR_COLORS.length];
              const isExpanded = expandedReviews[review.id];
              const isLong = review.body.length > 260;
              const isAnimating = animatingIds.has(review.id);

              return (
                <article
                  key={review.id}
                  className={`pb-8 border-b border-pearl/30 last:border-none transition-all duration-500 ${
                    isAnimating ? "opacity-0 translate-y-3 animate-[fadeSlideIn_0.5s_ease_forwards]" : "opacity-100"
                  }`}
                >
                  {/* Header Row */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0 ${colorClass}`}>
                      {review.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-0.5">
                        <span className="text-sm font-medium text-midnight">{review.author}</span>
                        {review.verified && (
                          <span className="text-[9px] tracking-wider uppercase bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <i className="ri-shield-check-line text-[10px]" /> Verified Purchase
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <i
                              key={i}
                              className={`text-[10px] ${
                                i < review.rating ? "ri-star-fill text-moon-gold-dark" : "ri-star-line text-pearl"
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-[10px] text-midnight/35">{review.date}</span>
                        <span className="text-[10px] text-midnight/35">·</span>
                        <span className="text-[10px] text-midnight/40">{review.location}</span>
                      </div>
                    </div>
                  </div>

                  {/* Purchased Size + Fit Tag */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="text-[10px] text-midnight/50 border border-pearl/60 px-2.5 py-1">
                      Size Purchased: <span className="font-medium text-midnight/70">{review.size}</span>
                    </span>
                    <span className={`text-[10px] px-2.5 py-1 font-medium rounded-full ${FIT_COLORS[review.fit]}`}>
                      {review.fit}
                    </span>
                  </div>

                  {/* Review Title */}
                  <p className="text-sm font-medium text-midnight mb-2">{review.title}</p>

                  {/* Review Body */}
                  <p className="text-sm text-midnight/60 leading-relaxed">
                    {isLong && !isExpanded ? review.body.slice(0, 260) + "…" : review.body}
                  </p>
                  {isLong && (
                    <button
                      onClick={() => toggleExpand(review.id)}
                      className="text-[11px] text-midnight/50 underline mt-1 cursor-pointer"
                    >
                      {isExpanded ? "Show less" : "Read more"}
                    </button>
                  )}

                  {/* Review Images */}
                  {review.images && review.images.length > 0 && (
                    <div className="flex gap-2 mt-4">
                      {review.images.map((img, i) => (
                        <div key={i} className="w-20 h-24 overflow-hidden border border-pearl/40">
                          <img
                            src={img}
                            alt={`Review photo ${i + 1}`}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Helpful */}
                  <div className="flex items-center gap-3 mt-4">
                    <span className="text-[10px] text-midnight/35">Was this helpful?</span>
                    <button
                      onClick={() => handleHelpful(review.id)}
                      className={`flex items-center gap-1.5 text-[11px] border px-2.5 py-1 transition-all cursor-pointer whitespace-nowrap rounded-full ${
                        helpfulVotes[review.id]
                          ? "border-midnight bg-midnight text-moonlight"
                          : "border-pearl text-midnight/40 hover:border-midnight hover:text-midnight"
                      }`}
                    >
                      <i className="ri-thumb-up-line text-[11px]" />
                      {review.helpful + (helpfulVotes[review.id] ? 1 : 0)}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>

          {/* Load More / Progress Area */}
          {filtered.length > PAGE_SIZE && (
            <div className="mt-10 flex flex-col items-center gap-5">
              {/* Progress bar */}
              <div className="w-full max-w-xs">
                <div className="flex justify-between text-[10px] text-midnight/40 mb-1.5">
                  <span>Showing {Math.min(visibleCount, filtered.length)} of {filtered.length} reviews</span>
                  <span>{progressPct}%</span>
                </div>
                <div className="h-px bg-pearl/60 w-full overflow-hidden">
                  <div
                    className="h-full bg-midnight transition-all duration-700 ease-out"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex items-center gap-4">
                {!allLoaded && (
                  <button
                    onClick={handleLoadMore}
                    className="text-xs tracking-widest uppercase text-midnight border border-midnight/30 px-8 py-3 hover:bg-midnight hover:text-moonlight hover:border-midnight transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    Load More
                    <span className="ml-2 text-[10px] opacity-60">+{Math.min(remaining, PAGE_SIZE)}</span>
                  </button>
                )}
                {visibleCount > PAGE_SIZE && (
                  <button
                    onClick={handleCollapse}
                    className="text-[11px] tracking-widest uppercase text-midnight/40 hover:text-midnight transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                  >
                    <i className="ri-arrow-up-s-line" /> Show Less
                  </button>
                )}
              </div>
            </div>
          )}

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <i className="ri-star-line text-3xl text-midnight/15 block mb-3" />
              <p className="text-sm text-midnight/40">No reviews match this filter.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
